import React, { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {  } from "react-icons/fa";
import axios from "axios";
import {
  Container, Row, Col, Button, Badge, Spinner,
  Pagination, Form, InputGroup
} from "react-bootstrap";
import {
  FaSearch, FaShoppingBag, FaHeart, FaStar,
  FaStarHalfAlt, FaFilter, FaTimes, FaEye,
  FaGem, FaFire, FaRegStar, FaArrowLeft, FaArrowRight,
  FaTshirt, FaFemale, FaChild, FaTag, FaClock, FaCheckCircle,
  FaSlidersH, FaDollarSign, FaSortAmountDown, FaSortAmountUp,
  FaRegHeart, FaShare, FaBolt, FaChevronDown, FaCrown, FaLeaf
} from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";
import { addToCart, addToWishlist, removeFromWishlist, isInWishlist } from "../utils/cartUtils";
import CatalogLoadingScreen from "./CatalogLoadingScreen";
import "./SearchResults.css";

const API_BASE = "https://whats.asbfashion.com/api";

const SearchResultsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const searchQuery = queryParams.get('q') || '';

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalResults, setTotalResults] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState("relevance");
  const [filterPrice, setFilterPrice] = useState({ min: "", max: "" });
  const [selectedCategory, setSelectedCategory] = useState("");
  const [categories, setCategories] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [wishlistStatus, setWishlistStatus] = useState({});
  const [addingToCart, setAddingToCart] = useState({});
  const [hoveredProduct, setHoveredProduct] = useState(null);
  const [priceRange, setPriceRange] = useState([0, 50000]);
  const [selectedRating, setSelectedRating] = useState(0);
  const [inStockOnly, setInStockOnly] = useState(false);
  const [expandedFilters, setExpandedFilters] = useState({
    categories: true,
    price: true,
    rating: true,
    availability: true
  });
  
  const itemsPerPage = 12;

  // Fetch search results
  const fetchSearchResults = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE}/search_products.php`, {
        params: {
          q: searchQuery,
          page: currentPage,
          limit: itemsPerPage,
          sort: sortBy,
          category: selectedCategory,
          min_price: filterPrice.min || priceRange[0],
          max_price: filterPrice.max || priceRange[1],
          min_rating: selectedRating,
          in_stock: inStockOnly
        }
      });

      if (response.data.success) {
        setProducts(response.data.products || []);
        setTotalResults(response.data.total || 0);
        
        const wishlistStatusMap = {};
        response.data.products.forEach(product => {
          wishlistStatusMap[product.id] = isInWishlist(product.id);
        });
        setWishlistStatus(wishlistStatusMap);
      } else {
        setProducts([]);
        setTotalResults(0);
      }
    } catch (error) {
      console.error("Search error:", error);
      setProducts([]);
      setTotalResults(0);
    } finally {
      setLoading(false);
    }
  }, [searchQuery, currentPage, sortBy, selectedCategory, filterPrice.min, filterPrice.max, priceRange, selectedRating, inStockOnly]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_BASE}/get_categories.php`);
      if (response.data.success) {
        setCategories(response.data.categories || []);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  useEffect(() => {
    if (searchQuery) {
      fetchSearchResults();
      fetchCategories();
    }
  }, [searchQuery, currentPage, sortBy, selectedCategory, filterPrice.min, filterPrice.max, priceRange, selectedRating, inStockOnly, fetchSearchResults]);

  const handleAddToCart = async (product, e) => {
    e.stopPropagation();
    setAddingToCart(prev => ({ ...prev, [product.id]: true }));
    
    const cartItem = {
      id: product.id,
      name: product.name,
      price: product.price,
      quantity: 1,
      image: product.image_url || '/images/placeholder.jpg',
      maxQuantity: product.stock_quantity || 999,
      category: product.category_name
    };
    
    const added = addToCart(cartItem);
    
    setTimeout(() => {
      setAddingToCart(prev => ({ ...prev, [product.id]: false }));
      if (added) {
        showAddToCartNotification(product.name);
      }
    }, 500);
  };

  const handleWishlistToggle = (product, e) => {
    e.stopPropagation();
    
    const productData = {
      id: product.id,
      name: product.name,
      price: product.price,
      old_price: product.old_price,
      image: product.image_url,
      category: product.category_name,
      rating: product.rating,
      discount: product.discount_percent
    };
    
    if (wishlistStatus[product.id]) {
      const removed = removeFromWishlist(product.id);
      if (removed) {
        setWishlistStatus(prev => ({ ...prev, [product.id]: false }));
      }
    } else {
      const added = addToWishlist(productData);
      if (added) {
        setWishlistStatus(prev => ({ ...prev, [product.id]: true }));
      }
    }
  };

  const showAddToCartNotification = (productName) => {
    const notification = document.createElement('div');
    notification.className = 'cart-notification';
    notification.innerHTML = `
      <div class="cart-notification-content">
        <svg class="check-icon" viewBox="0 0 24 24" width="20" height="20">
          <path fill="currentColor" d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/>
        </svg>
        <span>${productName} added to cart!</span>
      </div>
    `;
    document.body.appendChild(notification);
    setTimeout(() => {
      notification.classList.add('show');
      setTimeout(() => {
        notification.classList.remove('show');
        setTimeout(() => notification.remove(), 300);
      }, 2000);
    }, 10);
  };

  const handleProductClick = (productId) => {
    navigate(`/product/${productId}`);
  };

  const clearFilters = () => {
    setSelectedCategory("");
    setFilterPrice({ min: "", max: "" });
    setPriceRange([0, 50000]);
    setSelectedRating(0);
    setInStockOnly(false);
    setSortBy("relevance");
    setCurrentPage(1);
  };

  const getCategoryIcon = (categoryName) => {
    const name = categoryName?.toLowerCase() || '';
    if (name.includes('men')) return <FaTshirt />;
    if (name.includes('women')) return <FaFemale />;
    if (name.includes('kid')) return <FaChild />;
    return <FaTag />;
  };

  const renderStars = (rating) => {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    const emptyStars = 5 - Math.ceil(rating);
    
    return (
      <>
        {[...Array(fullStars)].map((_, i) => (
          <FaStar key={`full-${i}`} className="star-filled" />
        ))}
        {hasHalfStar && <FaStarHalfAlt className="star-half" />}
        {[...Array(emptyStars)].map((_, i) => (
          <FaRegStar key={`empty-${i}`} className="star-empty" />
        ))}
      </>
    );
  };

  const renderRatingFilter = (rating) => {
    return (
      <div className="rating-option" onClick={() => setSelectedRating(selectedRating === rating ? 0 : rating)}>
        <div className="rating-stars">
          {[...Array(5)].map((_, i) => (
            i < rating ? <FaStar key={i} className="star-filled" /> : <FaRegStar key={i} className="star-empty" />
          ))}
        </div>
        <span>& up</span>
        {selectedRating === rating && <FaCheckCircle className="rating-checked" />}
      </div>
    );
  };

  const formatPrice = (price) => {
    return `LKR ${parseFloat(price).toLocaleString()}`;
  };

  const totalPages = Math.ceil(totalResults / itemsPerPage);
  const activeFilterCount = (selectedCategory ? 1 : 0) + (filterPrice.min || filterPrice.max ? 1 : 0) + (selectedRating ? 1 : 0) + (inStockOnly ? 1 : 0);

  // Animation variants
  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.05, delayChildren: 0.1 }
    }
  };

  const toggleFilter = (filterName) => {
    setExpandedFilters(prev => ({
      ...prev,
      [filterName]: !prev[filterName]
    }));
  };

  return (
    <motion.div 
      className="search-results-page"
      initial="hidden"
      animate="visible"
      variants={staggerContainer}
    >
      {/* Hero Header */}
      <div className="search-hero">
        <div className="search-hero-overlay">
          <Container>
            <motion.div className="search-hero-content" variants={fadeInUp}>
              <div className="search-icon-wrapper">
                <FaSearch className="search-hero-icon" />
              </div>
              <h1 className="search-hero-title">
                Search Results for <span className="search-query">“{searchQuery}”</span>
              </h1>
              <p className="search-hero-stats">
                <FaCheckCircle className="stats-icon" />
                {totalResults} {totalResults === 1 ? 'product' : 'products'} found
              </p>
            </motion.div>
          </Container>
        </div>
      </div>

      <Container className="py-5">
        <Row>
          {/* Filters Sidebar - Desktop (Sticky) */}
          <Col lg={3} className="d-none d-lg-block">
            <div className="filters-sidebar sticky-top">
              <div className="filters-header">
                <h4><FaSlidersH className="filter-icon" /> Filters</h4>
                {activeFilterCount > 0 && (
                  <button onClick={clearFilters} className="clear-filters-btn">
                    <FaTimes /> Clear All ({activeFilterCount})
                  </button>
                )}
              </div>

              {/* Search within results */}
              <div className="filter-group search-within">
                <InputGroup className="search-within-input">
                  <InputGroup.Text>
                    <FaSearch />
                  </InputGroup.Text>
                  <Form.Control 
                    type="text" 
                    placeholder="Search within results..."
                  />
                </InputGroup>
              </div>

              {/* Category Filter */}
              <div className="filter-group">
                <div className="filter-group-header" onClick={() => toggleFilter('categories')}>
                  <h5><FaTag className="group-icon" /> Categories</h5>
                  <span className={`toggle-icon ${expandedFilters.categories ? 'expanded' : ''}`}>
                    <FaChevronDown />
                  </span>
                </div>
                {expandedFilters.categories && (
                  <div className="filter-group-content">
                    <div className="category-list">
                      <button
                        className={`category-filter ${selectedCategory === "" ? "active" : ""}`}
                        onClick={() => setSelectedCategory("")}
                      >
                        <FaTag className="cat-icon" />
                        All Categories
                        {selectedCategory === "" && <FaCheckCircle className="active-check" />}
                      </button>
                      {categories.map(cat => (
                        <button
                          key={cat.id}
                          className={`category-filter ${selectedCategory === cat.id ? "active" : ""}`}
                          onClick={() => setSelectedCategory(cat.id)}
                        >
                          {getCategoryIcon(cat.name)}
                          {cat.name}
                          {selectedCategory === cat.id && <FaCheckCircle className="active-check" />}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Price Filter */}
              <div className="filter-group">
                <div className="filter-group-header" onClick={() => toggleFilter('price')}>
                  <h5><FaDollarSign className="group-icon" /> Price Range</h5>
                  <span className={`toggle-icon ${expandedFilters.price ? 'expanded' : ''}`}>
                    <FaChevronDown />
                  </span>
                </div>
                {expandedFilters.price && (
                  <div className="filter-group-content">
                    <div className="price-range-slider">
                      <input
                        type="range"
                        min="0"
                        max="50000"
                        step="500"
                        value={priceRange[1]}
                        onChange={(e) => setPriceRange([priceRange[0], parseInt(e.target.value)])}
                        className="price-slider"
                      />
                      <div className="price-labels">
                        <span>LKR 0</span>
                        <span>LKR {priceRange[1].toLocaleString()}</span>
                      </div>
                    </div>
                    <div className="price-inputs">
                      <div className="price-input-group">
                        <span className="price-currency">LKR</span>
                        <input
                          type="number"
                          placeholder="Min"
                          value={filterPrice.min}
                          onChange={(e) => setFilterPrice(prev => ({ ...prev, min: e.target.value }))}
                          className="price-input"
                        />
                      </div>
                      <span className="price-separator">-</span>
                      <div className="price-input-group">
                        <span className="price-currency">LKR</span>
                        <input
                          type="number"
                          placeholder="Max"
                          value={filterPrice.max}
                          onChange={(e) => setFilterPrice(prev => ({ ...prev, max: e.target.value }))}
                          className="price-input"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Rating Filter */}
              <div className="filter-group">
                <div className="filter-group-header" onClick={() => toggleFilter('rating')}>
                  <h5><FaStar className="group-icon" /> Customer Rating</h5>
                  <span className={`toggle-icon ${expandedFilters.rating ? 'expanded' : ''}`}>
                    <FaChevronDown />
                  </span>
                </div>
                {expandedFilters.rating && (
                  <div className="filter-group-content">
                    <div className="rating-list">
                      {renderRatingFilter(4)}
                      {renderRatingFilter(3)}
                      {renderRatingFilter(2)}
                      {renderRatingFilter(1)}
                    </div>
                  </div>
                )}
              </div>

              {/* Availability Filter */}
              <div className="filter-group">
                <div className="filter-group-header" onClick={() => toggleFilter('availability')}>
                  <h5><FaCheckCircle className="group-icon" /> Availability</h5>
                  <span className={`toggle-icon ${expandedFilters.availability ? 'expanded' : ''}`}>
                    <FaChevronDown />
                  </span>
                </div>
                {expandedFilters.availability && (
                  <div className="filter-group-content">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={inStockOnly}
                        onChange={(e) => setInStockOnly(e.target.checked)}
                      />
                      <span className="checkbox-custom"></span>
                      In Stock Only
                    </label>
                  </div>
                )}
              </div>

              {/* Sort Options */}
              <div className="filter-group">
                <h5><FaSortAmountDown className="group-icon" /> Sort By</h5>
                <div className="sort-options-list">
                  {[
                    { value: "relevance", label: "🔥 Relevance", icon: <FaFire /> },
                    { value: "price_low", label: "💰 Price: Low to High", icon: <FaSortAmountDown /> },
                    { value: "price_high", label: "💰 Price: High to Low", icon: <FaSortAmountUp /> },
                    { value: "newest", label: "✨ Newest First", icon: <FaClock /> },
                    { value: "rating", label: "⭐ Top Rated", icon: <FaStar /> },
                    { value: "popular", label: "📈 Most Popular", icon: <FaBolt /> }
                  ].map(option => (
                    <button
                      key={option.value}
                      className={`sort-option ${sortBy === option.value ? 'active' : ''}`}
                      onClick={() => setSortBy(option.value)}
                    >
                      {option.icon}
                      {option.label}
                      {sortBy === option.value && <FaCheckCircle className="active-check" />}
                    </button>
                  ))}
                </div>
              </div>

              {/* Active Filters Summary */}
              {activeFilterCount > 0 && (
                <div className="active-filters-summary">
                  <p>Active Filters:</p>
                  <div className="active-filter-tags">
                    {selectedCategory && (
                      <span className="filter-tag">
                        {categories.find(c => c.id === selectedCategory)?.name}
                        <button onClick={() => setSelectedCategory("")}>×</button>
                      </span>
                    )}
                    {(filterPrice.min || filterPrice.max || priceRange[1] < 50000) && (
                      <span className="filter-tag">
                        Price: {filterPrice.min || 0} - {filterPrice.max || priceRange[1]}
                        <button onClick={() => {
                          setFilterPrice({ min: "", max: "" });
                          setPriceRange([0, 50000]);
                        }}>×</button>
                      </span>
                    )}
                    {selectedRating > 0 && (
                      <span className="filter-tag">
                        {selectedRating}+ Stars
                        <button onClick={() => setSelectedRating(0)}>×</button>
                      </span>
                    )}
                    {inStockOnly && (
                      <span className="filter-tag">
                        In Stock Only
                        <button onClick={() => setInStockOnly(false)}>×</button>
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          </Col>

          {/* Mobile Filter Toggle */}
          <Col xs={12} className="d-lg-none mb-4">
            <button className="mobile-filter-btn" onClick={() => setShowFilters(!showFilters)}>
              <FaFilter /> Filters & Sorting
              {activeFilterCount > 0 && <span className="filter-count">{activeFilterCount}</span>}
            </button>
          </Col>

          {/* Products Grid */}
          <Col lg={9}>
            {/* Sort Bar */}
            <motion.div className="sort-bar" variants={fadeInUp}>
              <div className="results-count">
                <FaSearch className="results-icon" />
                Showing <strong>{totalResults > 0 ? ((currentPage - 1) * itemsPerPage) + 1 : 0}</strong> - 
                <strong>{Math.min(currentPage * itemsPerPage, totalResults)}</strong> of 
                <strong>{totalResults}</strong> results
              </div>
              <div className="sort-options-desktop">
                <span>Sort by:</span>
                <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="sort-select-small">
                  <option value="relevance">🔥 Relevance</option>
                  <option value="price_low">💰 Price: Low to High</option>
                  <option value="price_high">💰 Price: High to Low</option>
                  <option value="newest">✨ Newest First</option>
                  <option value="rating">⭐ Top Rated</option>
                  <option value="popular">📈 Most Popular</option>
                </select>
              </div>
            </motion.div>

            {/* Loading State */}
            {loading ? (
              <motion.div variants={fadeInUp}>
                <CatalogLoadingScreen subtitle="Searching for amazing products..." />
              </motion.div>
            ) : products.length > 0 ? (
              <>
                <Row className="g-4">
                  {products.map((product, index) => (
                    <Col key={product.id} xs={12} sm={6} lg={4}>
                      <motion.div
                        variants={fadeInUp}
                        whileHover={{ y: -8 }}
                        onHoverStart={() => setHoveredProduct(product.id)}
                        onHoverEnd={() => setHoveredProduct(null)}
                        className="product-card"
                        onClick={() => handleProductClick(product.id)}
                      >
                        <div className="product-image-wrapper">
                          <img
                            src={product.image_url || '/images/placeholder.jpg'}
                            alt={product.name}
                            className="product-image"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleProductClick(product.id);
                            }}
                            onTouchEnd={(e) => {
                              e.stopPropagation();
                              handleProductClick(product.id);
                            }}
                            loading="lazy"
                            role="button"
                            tabIndex={0}
                            onError={(e) => e.target.src = '/images/placeholder.jpg'}
                          />
                          {product.discount_percent > 0 && (
                            <Badge className="discount-badge">
                              <FaFire className="discount-icon" /> -{product.discount_percent}%
                            </Badge>
                          )}
                          {product.is_new_arrival && (
                            <Badge className="new-badge">
                              <FaClock /> New
                            </Badge>
                          )}
                          {product.is_trending && (
                            <Badge className="trending-badge">
                              <FaFire /> Trending
                            </Badge>
                          )}
                          <div className={`product-overlay ${hoveredProduct === product.id ? 'show' : ''}`}>
                            <button
                              className="overlay-btn wishlist-btn"
                              onClick={(e) => handleWishlistToggle(product, e)}
                            >
                              <FaHeart className={wishlistStatus[product.id] ? 'active' : ''} />
                              <span>Wishlist</span>
                            </button>
                            <button
                              className="overlay-btn quick-view-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleProductClick(product.id);
                              }}
                            >
                              <FaEye />
                              <span>Quick View</span>
                            </button>
                            <button
                              className="overlay-btn share-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigator.clipboard.writeText(window.location.origin + `/#/product/${product.id}`);
                                showAddToCartNotification('Link copied!');
                              }}
                            >
                              <FaShare />
                              <span>Share</span>
                            </button>
                          </div>
                        </div>
                        <div className="product-info">
                          <h3 className="product-title">{product.name}</h3>
                          {product.brand && (
                            <div className="product-brand">
                              <FaGem className="brand-icon" />
                              <span>{product.brand}</span>
                            </div>
                          )}
                          <div className="product-rating">
                            {renderStars(product.rating || 0)}
                            <span className="rating-count">({product.review_count || 0})</span>
                          </div>
                          <div className="product-price">
                            <span className="current-price">{formatPrice(product.price)}</span>
                            {product.old_price && product.old_price > product.price && (
                              <span className="old-price">{formatPrice(product.old_price)}</span>
                            )}
                          </div>
                          <button
                            className="add-to-cart-btn"
                            onClick={(e) => handleAddToCart(product, e)}
                            disabled={addingToCart[product.id]}
                          >
                            {addingToCart[product.id] ? (
                              <Spinner animation="border" size="sm" />
                            ) : (
                              <><FaShoppingBag /> Add to Cart</>
                            )}
                          </button>
                        </div>
                      </motion.div>
                    </Col>
                  ))}
                </Row>

                {/* Pagination */}
                {totalPages > 1 && (
                  <motion.div className="pagination-wrapper" variants={fadeInUp}>
                    <Pagination>
                      <Pagination.Prev
                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                        disabled={currentPage === 1}
                      >
                        <FaArrowLeft />
                      </Pagination.Prev>
                      {[...Array(Math.min(5, totalPages))].map((_, i) => {
                        let pageNum;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (currentPage >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = currentPage - 2 + i;
                        }
                        return (
                          <Pagination.Item
                            key={pageNum}
                            active={pageNum === currentPage}
                            onClick={() => setCurrentPage(pageNum)}
                          >
                            {pageNum}
                          </Pagination.Item>
                        );
                      })}
                      <Pagination.Next
                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                        disabled={currentPage === totalPages}
                      >
                        <FaArrowRight />
                      </Pagination.Next>
                    </Pagination>
                  </motion.div>
                )}
              </>
            ) : (
              <motion.div className="no-results" variants={fadeInUp}>
                <div className="no-results-icon-wrapper">
                  <FaSearch className="no-results-icon" />
                </div>
                <h3>No products found</h3>
                <p>We couldn't find any products matching <strong>“{searchQuery}”</strong></p>
                <div className="no-results-suggestions">
                  <h4><FaTag /> Suggestions:</h4>
                  <ul>
                    <li>Check your spelling</li>
                    <li>Try more general keywords</li>
                    <li>Try different keywords</li>
                    <li>Browse our categories</li>
                  </ul>
                </div>
                <Button variant="danger" onClick={() => navigate('/')} className="back-home-btn">
                  Browse All Products
                </Button>
              </motion.div>
            )}
          </Col>
        </Row>
      </Container>

      {/* Mobile Filters Modal */}
      <AnimatePresence>
        {showFilters && (
          <>
            <motion.div
              className="mobile-filters-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowFilters(false)}
            />
            <motion.div
              className="mobile-filters-modal"
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'tween', duration: 0.3 }}
            >
              <div className="mobile-filters-header">
                <h4><FaSlidersH /> Filters & Sorting</h4>
                <button onClick={() => setShowFilters(false)}><FaTimes /></button>
              </div>
              <div className="mobile-filters-body">
                {/* Categories */}
                <div className="filter-group">
                  <h5>Categories</h5>
                  <div className="category-list">
                    <button
                      className={`category-filter ${selectedCategory === "" ? "active" : ""}`}
                      onClick={() => setSelectedCategory("")}
                    >
                      All Categories
                    </button>
                    {categories.map(cat => (
                      <button
                        key={cat.id}
                        className={`category-filter ${selectedCategory === cat.id ? "active" : ""}`}
                        onClick={() => setSelectedCategory(cat.id)}
                      >
                        {cat.name}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Price Range */}
                <div className="filter-group">
                  <h5>Price Range</h5>
                  <div className="price-inputs">
                    <input
                      type="number"
                      placeholder="Min"
                      value={filterPrice.min}
                      onChange={(e) => setFilterPrice(prev => ({ ...prev, min: e.target.value }))}
                      className="price-input"
                    />
                    <span>-</span>
                    <input
                      type="number"
                      placeholder="Max"
                      value={filterPrice.max}
                      onChange={(e) => setFilterPrice(prev => ({ ...prev, max: e.target.value }))}
                      className="price-input"
                    />
                  </div>
                </div>

                {/* Rating Filter */}
                <div className="filter-group">
                  <h5>Rating</h5>
                  <div className="rating-list">
                    {[4, 3, 2, 1].map(rating => (
                      <div key={rating} className="rating-option" onClick={() => setSelectedRating(selectedRating === rating ? 0 : rating)}>
                        <div className="rating-stars">
                          {[...Array(5)].map((_, i) => (
                            i < rating ? <FaStar key={i} className="star-filled" /> : <FaRegStar key={i} className="star-empty" />
                          ))}
                        </div>
                        <span>& up</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Sort By */}
                <div className="filter-group">
                  <h5>Sort By</h5>
                  <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="sort-select">
                    <option value="relevance">🔥 Relevance</option>
                    <option value="price_low">💰 Price: Low to High</option>
                    <option value="price_high">💰 Price: High to Low</option>
                    <option value="newest">✨ Newest First</option>
                    <option value="rating">⭐ Top Rated</option>
                  </select>
                </div>

                {/* In Stock */}
                <div className="filter-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={inStockOnly}
                      onChange={(e) => setInStockOnly(e.target.checked)}
                    />
                    <span className="checkbox-custom"></span>
                    In Stock Only
                  </label>
                </div>
              </div>
              <div className="mobile-filters-footer">
                <button className="clear-btn" onClick={clearFilters}>Clear All</button>
                <button className="apply-btn" onClick={() => setShowFilters(false)}>Apply Filters</button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// Add missing import for FaChevronDown


export default SearchResultsPage;